import { create } from 'zustand';
import { containersService, type SyncSummary } from '@/deploy/services/containers.service';
import type { MinecraftContainer } from '@/deploy/types';

interface ContainersState {
  /**
   * Containers ACTIFS du projet : les pages Release, Suivi, Environnements et le badge d'activité
   * s'en servent, un container archivé n'y figure jamais.
   */
  containers: MinecraftContainer[];
  /** Containers archivés du projet, chargés à part (page Container MC, « Afficher les archivés »). */
  archived: MinecraftContainer[];
  loading: boolean;
  syncing: boolean;
  loadingProjectId: string | null;
  loadedProjectId: string | null;
  archivedLoadedProjectId: string | null;
  error: string | null;
  lastSync: SyncSummary | null;
  // timestamps (ms epoch) auxquels le cooldown se termine — pour disable les boutons localement
  cooldownAllUntil: number;
  cooldownByCode: Record<string, number>;

  load: (projectId: string, force?: boolean) => Promise<void>;
  loadArchived: (projectId: string, force?: boolean) => Promise<void>;
  refreshOne: (projectId: string, code: string) => Promise<void>;
  syncAll: (projectId: string) => Promise<SyncSummary>;
  syncOne: (projectId: string, code: string) => Promise<void>;
  /** Archive (lève l'erreur du CICD, ex. release en cours) et passe le container dans `archived`. */
  archive: (projectId: string, code: string) => Promise<MinecraftContainer>;
  /** Désarchive et remet le container dans `containers`. */
  unarchive: (projectId: string, code: string) => Promise<MinecraftContainer>;
  refreshCooldown: (projectId: string) => Promise<void>;
  reset: () => void;
}

const COOLDOWN_MS = 60_000;

/** Même ordre que le CICD (par nom affiché). */
function byName(list: MinecraftContainer[]): MinecraftContainer[] {
  return [...list].sort((a, b) => (a.displayName ?? a.code).localeCompare(b.displayName ?? b.code));
}

export const useContainersStore = create<ContainersState>((set, get) => ({
  containers: [],
  archived: [],
  loading: false,
  syncing: false,
  loadingProjectId: null,
  loadedProjectId: null,
  archivedLoadedProjectId: null,
  error: null,
  lastSync: null,
  cooldownAllUntil: 0,
  cooldownByCode: {},

  load: async (projectId, force) => {
    if (!projectId) return;
    if (!force && get().loadedProjectId === projectId) return;
    set({ loading: true, loadingProjectId: projectId, error: null });
    try {
      const items = await containersService.list(projectId);
      // Ne pas écraser si l'utilisateur a entre-temps switché de projet
      if (get().loadingProjectId !== projectId) return;
      // Le CICD n'envoie pas les archivés ici ; filtrés aussi par prudence
      set({ containers: items.filter((c) => !c.archived), loadedProjectId: projectId });
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Erreur de chargement' });
    } finally {
      set({ loading: false, loadingProjectId: null });
    }
  },

  loadArchived: async (projectId, force) => {
    if (!projectId) return;
    if (!force && get().archivedLoadedProjectId === projectId) return;
    try {
      const items = await containersService.list(projectId, { includeArchived: true });
      // Un CICD d'avant l'archivage n'envoie pas le champ : aucun archivé
      set({ archived: items.filter((c) => c.archived === true), archivedLoadedProjectId: projectId });
    } catch {
      // Best effort : l'interrupteur affiche (0) et la liste des actifs reste utilisable
      set({ archived: [], archivedLoadedProjectId: projectId });
    }
  },

  refreshOne: async (projectId, code) => {
    try {
      const updated = await containersService.get(projectId, code);
      set((s) => ({
        containers: s.containers.map((c) => (c.code === code ? updated : c)),
        archived: s.archived.map((c) => (c.code === code ? updated : c)),
      }));
    } catch (e) {
      set({ error: e instanceof Error ? e.message : 'Erreur refresh' });
    }
  },

  syncAll: async (projectId) => {
    set({ syncing: true, error: null });
    try {
      const summary = await containersService.syncAll(projectId);
      set((s) => ({
        lastSync: summary,
        cooldownAllUntil: Date.now() + COOLDOWN_MS,
        // Cooldown s'applique aussi à chaque container individuellement après un sync-all
        cooldownByCode: Object.fromEntries(s.containers.map((c) => [c.code, Date.now() + COOLDOWN_MS])),
      }));
      // Refetch list après sync
      const items = await containersService.list(projectId);
      set({ containers: items.filter((c) => !c.archived) });
      return summary;
    } finally {
      set({ syncing: false });
    }
  },

  syncOne: async (projectId, code) => {
    set({ syncing: true, error: null });
    try {
      await containersService.syncOne(projectId, code);
      set((s) => ({
        cooldownByCode: { ...s.cooldownByCode, [code]: Date.now() + COOLDOWN_MS },
      }));
      await get().refreshOne(projectId, code);
    } finally {
      set({ syncing: false });
    }
  },

  archive: async (projectId, code) => {
    const updated = await containersService.archive(projectId, code);
    set((s) => ({
      containers: s.containers.filter((c) => c.code !== code),
      archived: byName([...s.archived.filter((c) => c.code !== code), updated]),
    }));
    return updated;
  },

  unarchive: async (projectId, code) => {
    const updated = await containersService.unarchive(projectId, code);
    set((s) => ({
      archived: s.archived.filter((c) => c.code !== code),
      // Liste des actifs pas encore chargée pour ce projet : elle le sera à l'ouverture d'une page
      containers: s.loadedProjectId === projectId
        ? byName([...s.containers.filter((c) => c.code !== code), updated])
        : s.containers,
    }));
    return updated;
  },

  refreshCooldown: async (projectId) => {
    try {
      const cd = await containersService.syncCooldown(projectId);
      const now = Date.now();
      set({
        cooldownAllUntil: cd.allMs > 0 ? now + cd.allMs : 0,
        cooldownByCode: Object.fromEntries(
          Object.entries(cd.perContainerMs).map(([code, ms]) => [code, now + ms])
        ),
      });
    } catch { /* best effort */ }
  },

  reset: () => set({
    containers: [], archived: [], loadedProjectId: null, archivedLoadedProjectId: null,
    error: null, lastSync: null, cooldownAllUntil: 0, cooldownByCode: {},
  }),
}));
